import { useCallback, useContext, useEffect, useState } from 'react';
import { AnalyticsContextType } from '~/libs/analytics/components/Analytics';
import { PageContextType, PageContext } from '~/libs/analytics/components/Page';
import { Action } from '~/libs/analytics/types/Action';
import {
  EventProperties,
  EventPropertiesSnakeCase,
} from '~/libs/analytics/types/EventProperties';
import {
  camelCaseToKebabCase,
  kebabCase,
} from '~/libs/analytics/utils/caseConversion';
import eventPropertiesHasUndefinedValue from '~/libs/analytics/utils/eventPropertiesHasUndefinedValue';
import exceptionLogger from '~/libs/exception-logger';
import mapCamelCaseObjectToSnakeCase from '~/libs/analytics/utils/mapCamelCaseObjectToSnakeCase';
import getOptions, { Destination } from '~/libs/analytics/utils/options';
import useAnalytics from './useAnalytics';
import useFingerprint from './useFingerprint';

interface Options {
  destination?: Destination;
  server?: boolean;
  sendThroughProxy?: false | 'only' | true;
}

export type TrackAction = (
  action: Action,
  eventProperties?: Partial<EventProperties>,
  options?: Options,
) => Promise<void>;

export interface UseTrackActionProps {
  context?: string;
  object: string;
  eventProperties?: Partial<EventProperties>;
}

/**
 * Generator to make a wrapper for Segment Analytics track method.
 * This wrapper will autofill data and keep our events consistent.
 * @param {camelCase | kebab-case string} [context] - Stage or step level for event. camelCase or kebab-case
 * @param {camelCase | kebab-case string} object - Anything that represents an UI/UX element.
 * @param {EventProperties} [eventProperties] - Extra properties to send when the trackAction is called.
 * @return {TrackAction} - A method that will call the segment analytics track method
 */
const useTrackAction = ({
  context,
  object,
  eventProperties = {},
}: UseTrackActionProps): TrackAction => {
  const analytics = useAnalytics();
  const page = useContext(PageContext);
  const [eventPropertiesSaved, setEventPropertiesSaved] =
    useState<Partial<EventProperties>>(eventProperties);
  useEffect(() => {
    const eventPropertiesKeys = Object.keys(eventProperties);
    if (
      !eventPropertiesKeys.some((key) => {
        return eventProperties[key] !== eventPropertiesSaved[key];
      })
    ) {
      return;
    }
    setEventPropertiesSaved({ ...eventProperties });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventProperties]);

  return useMakeTrackAction({
    context,
    object,
    analytics,
    page,
    eventProperties: eventPropertiesSaved,
  });
};

const useMakeTrackAction = ({
  context,
  object,
  analytics,
  page,
  eventProperties = {},
}: Omit<UseTrackActionProps, 'objects'> & {
  analytics?: AnalyticsContextType | null;
  page: PageContextType;
}) => {
  const fingerprint = useFingerprint();

  return useCallback(
    async (
      action: Action,
      eventPropertiesFromAction: Partial<EventProperties> = {},
      options: Options = {},
    ) => {
      // Don't send any events if we are waiting on data.
      if (
        page.organizationId === undefined ||
        analytics?.circleciAnonymousId === null ||
        page.userId === undefined ||
        eventPropertiesHasUndefinedValue({
          ...eventProperties,
          ...eventPropertiesFromAction,
        })
      ) {
        return;
      }

      const name = camelCaseToKebabCase(context, object);

      const properties = {
        sender: 'webapp',
        ...page,
        ...eventProperties,
        ...eventPropertiesFromAction,
        action,
        circleciAnonymousId: analytics?.circleciAnonymousId,
        googleClientId: analytics?.googleClientId,
        isGrowthAnalytics: false,
        browserFingerprint: fingerprint,
      };
      try {
        if (!kebabCase.test(name)) {
          throw new Error(
            `${
              context ? 'Context and ' : ''
            }Object need to be camelCase or kebab-case, ${
              context ? `Context: ${context}, ` : ''
            }object: ${object}`,
          );
        }

        const propertiesSnaked =
          mapCamelCaseObjectToSnakeCase<EventPropertiesSnakeCase>(properties);
        if (shouldSendProxyEvent({ page, options })) {
          await analytics?.server?.track(name, {
            ...propertiesSnaked,
            is_growth_analytics: true,
          });
        }
        if (shouldSendSegmentEvent({ page, options })) {
          let resolver: (value?: undefined) => void;
          const waitForSegment = new Promise<undefined>(
            (resolve) => (resolver = resolve),
          );
          analytics?.segment?.track(
            name,
            propertiesSnaked,
            getOptions({ destination: options.destination }),
            () => {
              resolver();
            },
          );
          await waitForSegment;
        }
      } catch (error) {
        exceptionLogger().error(
          'Track Action',
          name,
          properties || {},
          options || {},
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          error?.message,
        );
        if (process.env.NODE_ENV === 'development') {
          throw error;
        }
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, analytics, eventProperties],
  );
};

const shouldSendProxyEvent = ({
  page,
  options,
}: {
  page: PageContextType;
  options: Options;
}): boolean => {
  const isSegmentLoaded = page.anonymousId !== undefined;
  const shouldSendThroughProxyIfSegmentIsNotLoaded = Boolean(
    options?.server || options?.sendThroughProxy === true,
  );
  const shouldAlwaysSendThroughProxy = options?.sendThroughProxy === 'only';
  return Boolean(
    (!isSegmentLoaded && shouldSendThroughProxyIfSegmentIsNotLoaded) ||
      shouldAlwaysSendThroughProxy,
  );
};

const shouldSendSegmentEvent = ({
  page,
  options,
}: {
  page: PageContextType;
  options: Options;
}): boolean => {
  const isSegmentLoaded = page.anonymousId !== undefined;
  const shouldOnlySendThroughProxy = options?.sendThroughProxy === 'only';
  return Boolean(isSegmentLoaded && !shouldOnlySendThroughProxy);
};

export default useTrackAction;
